/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../logging/Log";
import type { TInputEvent, InputInterceptor } from "./types/TInputEvent";

/**
 * Policy controlling what happens when an interceptor throws.
 *
 * - `"skip"` (default) — log the error and continue to the next interceptor.
 *   Resilient, but a broken interceptor that should have blocked an event
 *   (e.g., RepeatGuardInterceptor) will be silently bypassed.
 * - `"stop"` — log the error and halt the chain. The event will not be
 *   routed to any screen, preventing potentially unguarded actions from
 *   reaching screen controllers after a middleware failure.
 */
export type InterceptorErrorPolicy = "skip" | "stop";

/**
 * Unified middleware pipeline for input events.
 * Interceptors are executed in registration order. If any interceptor
 * returns `true` or sets `event.stopped = true`, the chain halts.
 *
 * ## FR-08 — configurable error policy
 *
 * The `errorPolicy` constructor option controls how interceptor errors are
 * handled. Defaults to `"skip"` to preserve the original resilient behaviour.
 * Use `"stop"` in contexts where a failing interceptor must not allow events
 * to pass through unguarded.
 */
export class InterceptorChain {
  private static TAG: string = "InterceptorChain";

  private mInterceptors: { name: string; fn: InputInterceptor }[] = [];
  private mErrorPolicy: InterceptorErrorPolicy;

  constructor(errorPolicy: InterceptorErrorPolicy = "skip") {
    this.mErrorPolicy = errorPolicy;
  }

  /**
   * Registers an interceptor under the given name, after every interceptor
   * already registered.
   *
   * @param name - Unique name used to identify the interceptor for removal.
   * @param fn - The interceptor function.
   */
  public use(name: string, fn: InputInterceptor): void {
    this.mInterceptors.push({ name, fn });
  }

  /**
   * Registers an interceptor ahead of every interceptor already registered,
   * and ahead of any registered later by {@link use}.
   *
   * `use()` appends, so "register it first" is only as reliable as the order
   * the registrations happen to run in — and under React it is not reliable
   * at all: hooks called above an effect register before it, and a child
   * component's effects run before its parent's. An interceptor that must
   * see an event before anything else, such as the EAS input block, has to
   * say so rather than depend on that order.
   *
   * Re-prepending registers ahead of a previous prepend, so the most recent
   * caller wins. There is one such interceptor today, so no precedence
   * scheme is warranted.
   *
   * @param name - Unique name used to identify the interceptor for removal.
   * @param fn - The interceptor function.
   */
  public useFirst(name: string, fn: InputInterceptor): void {
    this.mInterceptors.unshift({ name, fn });
  }

  /**
   * Removes the interceptor registered under `name`.
   *
   * @param name - The name the interceptor was registered with.
   */
  public remove(name: string): void {
    this.mInterceptors = this.mInterceptors.filter((i) => i.name !== name);
  }

  /**
   * Runs all registered interceptors against `event` in order.
   * Halts early if any interceptor returns `true` or sets `event.stopped`.
   * On error, applies `errorPolicy` to decide whether to skip or stop.
   *
   * @param event - The input event to process.
   * @returns The (possibly mutated) event.
   */
  public process(event: TInputEvent): TInputEvent {
    for (const interceptor of this.mInterceptors) {
      try {
        const consumed = interceptor.fn(event);
        if (consumed === true || event.stopped) {
          event.stopped = true;
          return event;
        }
      } catch (error: unknown) {
        Log.e(
          InterceptorChain.TAG,
          `process() — error in interceptor "${interceptor.name}"`,
          error
        );
        if (this.mErrorPolicy === "stop") {
          event.stopped = true;
          return event;
        }
        // "skip" policy: log and continue to the next interceptor.
      }
    }
    return event;
  }
}
